<template>
  <canvas ref="canvas" class="hero-network" aria-hidden="true"></canvas>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface NetworkNode {
  x: number
  y: number
  vx: number
  vy: number
  flash: number
  beaconPhase: number | null
}

interface Pulse {
  from: number
  to: number
  progress: number
  hops: number
}

const LINE_COLOR = '96, 165, 250' // --primary-400
const GLOW_COLOR = '147, 197, 253' // --primary-300
const PULSE_SPEED = 160 // px/s
const PULSE_TAIL = 0.35 // fração da aresta ocupada pelo rastro
const MAX_HOPS = 4
const BEACON_RATIO = 0.08 // nós que piscam como LED

const canvas = ref<HTMLCanvasElement | null>(null)

let ctx: CanvasRenderingContext2D | null = null
let width = 0
let height = 0
let linkDistance = 140
let maxPulses = 6
let nodes: NetworkNode[] = []
let neighbors: number[][] = []
let pulses: Pulse[] = []
let frameId = 0
let lastTime = 0
let elapsed = 0
let isVisible = true
let reducedMotion = false
let resizeObserver: ResizeObserver | undefined
let intersectionObserver: IntersectionObserver | undefined

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(Math.max((x - edge0) / (edge1 - edge0), 0), 1)
  return t * t * (3 - 2 * t)
}

// Atenua a rede atrás do texto central para não prejudicar a leitura
const textFade = (x: number, y: number) => {
  const dx = (x - width / 2) / (width * 0.32)
  const dy = (y - height / 2) / (height * 0.4)
  return 0.3 + 0.7 * smoothstep(0.5, 1.3, Math.sqrt(dx * dx + dy * dy))
}

const createNodes = () => {
  const isMobile = width < 768
  linkDistance = isMobile ? 120 : 170
  maxPulses = isMobile ? 3 : 7
  const count = Math.round(Math.min(Math.max((width * height) / 10000, 32), 140))

  nodes = Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2
    const speed = 6 + Math.random() * 12
    return {
      x: Math.random() * width,
      y: Math.random() * height,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      flash: 0,
      beaconPhase: Math.random() < BEACON_RATIO ? Math.random() * Math.PI * 2 : null
    }
  })
  pulses = []
}

const resize = () => {
  const el = canvas.value
  if (!el || !ctx) return

  const rect = el.getBoundingClientRect()
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  width = rect.width
  height = rect.height
  el.width = Math.round(width * dpr)
  el.height = Math.round(height * dpr)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  createNodes()
  if (reducedMotion) draw(0)
}

const moveNodes = (dt: number) => {
  for (const node of nodes) {
    node.x += node.vx * dt
    node.y += node.vy * dt
    if (node.x < 0 || node.x > width) node.vx *= -1
    if (node.y < 0 || node.y > height) node.vy *= -1
    node.x = Math.min(Math.max(node.x, 0), width)
    node.y = Math.min(Math.max(node.y, 0), height)
    node.flash = Math.max(0, node.flash - dt * 1.4)
  }
}

const drawGlow = (x: number, y: number, radius: number, alpha: number) => {
  if (!ctx || alpha <= 0.01) return
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius)
  gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`)
  gradient.addColorStop(0.25, `rgba(${GLOW_COLOR}, ${alpha * 0.8})`)
  gradient.addColorStop(1, `rgba(${GLOW_COLOR}, 0)`)
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
}

const drawLinks = () => {
  if (!ctx) return
  neighbors = nodes.map(() => [])
  ctx.lineWidth = 1

  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i]
    for (let j = i + 1; j < nodes.length; j++) {
      const b = nodes[j]
      const dx = a.x - b.x
      const dy = a.y - b.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      if (distance > linkDistance) continue

      neighbors[i].push(j)
      neighbors[j].push(i)

      const alpha = (1 - distance / linkDistance) * 0.5 * textFade((a.x + b.x) / 2, (a.y + b.y) / 2)
      ctx.strokeStyle = `rgba(${LINE_COLOR}, ${alpha})`
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
    }
  }
}

const drawNodes = () => {
  if (!ctx) return

  for (const node of nodes) {
    const fade = textFade(node.x, node.y)
    ctx.fillStyle = `rgba(${LINE_COLOR}, ${0.65 * fade})`
    ctx.beginPath()
    ctx.arc(node.x, node.y, 1.6, 0, Math.PI * 2)
    ctx.fill()

    // LED intermitente: piscadas curtas em intervalos regulares
    const beacon = node.beaconPhase === null
      ? 0
      : Math.pow(Math.max(0, Math.sin(elapsed * 1.8 + node.beaconPhase)), 12)
    const light = Math.max(node.flash, beacon)
    if (light > 0) drawGlow(node.x, node.y, 3 + 9 * light, light * 0.9 * fade)
  }
}

const spawnPulse = () => {
  const candidates = nodes.map((_, i) => i).filter(i => neighbors[i]?.length)
  if (!candidates.length) return
  const from = candidates[Math.floor(Math.random() * candidates.length)]
  const options = neighbors[from]
  const to = options[Math.floor(Math.random() * options.length)]
  nodes[from].flash = Math.max(nodes[from].flash, 0.6)
  pulses.push({ from, to, progress: 0, hops: 0 })
}

const updatePulses = (dt: number) => {
  if (pulses.length < maxPulses && Math.random() < dt * 1.2) spawnPulse()

  for (let i = pulses.length - 1; i >= 0; i--) {
    const pulse = pulses[i]
    const a = nodes[pulse.from]
    const b = nodes[pulse.to]
    const length = Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1)
    pulse.progress += (PULSE_SPEED * dt) / length

    if (pulse.progress < 1 + PULSE_TAIL) continue

    // Sinal chegou: acende o nó e, às vezes, segue para um vizinho
    b.flash = 1
    const next = (neighbors[pulse.to] || []).filter(n => n !== pulse.from)
    if (pulse.hops < MAX_HOPS && next.length && Math.random() < 0.65) {
      pulse.from = pulse.to
      pulse.to = next[Math.floor(Math.random() * next.length)]
      pulse.progress = 0
      pulse.hops++
    } else {
      pulses.splice(i, 1)
    }
  }
}

const drawPulses = () => {
  if (!ctx) return

  for (const pulse of pulses) {
    const a = nodes[pulse.from]
    const b = nodes[pulse.to]
    const head = Math.min(pulse.progress, 1)
    const tail = Math.max(pulse.progress - PULSE_TAIL, 0)
    if (tail >= 1) continue

    const hx = a.x + (b.x - a.x) * head
    const hy = a.y + (b.y - a.y) * head
    const tx = a.x + (b.x - a.x) * tail
    const ty = a.y + (b.y - a.y) * tail
    const fade = textFade(hx, hy)

    const trail = ctx.createLinearGradient(tx, ty, hx, hy)
    trail.addColorStop(0, `rgba(${GLOW_COLOR}, 0)`)
    trail.addColorStop(1, `rgba(${GLOW_COLOR}, ${0.9 * fade})`)
    ctx.strokeStyle = trail
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(tx, ty)
    ctx.lineTo(hx, hy)
    ctx.stroke()

    if (pulse.progress <= 1) drawGlow(hx, hy, 8, fade)
  }
}

const draw = (dt: number) => {
  if (!ctx) return
  ctx.clearRect(0, 0, width, height)
  moveNodes(dt)
  drawLinks()
  drawPulses()
  drawNodes()
}

const loop = (time: number) => {
  const dt = lastTime ? Math.min((time - lastTime) / 1000, 0.05) : 0
  lastTime = time
  elapsed += dt
  updatePulses(dt)
  draw(dt)
  frameId = requestAnimationFrame(loop)
}

const start = () => {
  if (reducedMotion || frameId || !isVisible) return
  lastTime = 0
  frameId = requestAnimationFrame(loop)
}

const stop = () => {
  cancelAnimationFrame(frameId)
  frameId = 0
}

onMounted(() => {
  const el = canvas.value
  ctx = el?.getContext('2d') ?? null
  if (!el || !ctx) return

  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  // Sem animação: mostra a rede parada com alguns LEDs acesos
  if (reducedMotion) elapsed = Math.PI / 3.6

  resize()
  resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(el)

  intersectionObserver = new IntersectionObserver(([entry]) => {
    isVisible = entry.isIntersecting
    if (isVisible) start()
    else stop()
  })
  intersectionObserver.observe(el)

  start()
})

onBeforeUnmount(() => {
  stop()
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
})
</script>

<style scoped>
.hero-network {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
